import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, Clock, CheckCircle, Trash2, ArrowLeft, 
  RefreshCw, Mail, Calendar, Building2, User, Play,
  XCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getVersionString } from '../config/version';

const AdminTrialManagementPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  
  const [trialPartners, setTrialPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningCheck, setRunningCheck] = useState(false);
  const [lastCheckResult, setLastCheckResult] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadTrialPartners();
  }, [token, navigate]);

  const loadTrialPartners = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/trial-partners`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setTrialPartners(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const runTrialCheck = async () => {
    setRunningCheck(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/vertrieb/check-trial-expiry`,
        { method: 'POST' }
      );
      const result = await response.json();
      setLastCheckResult(result);
      loadTrialPartners(); // Reload after check
    } catch (error) {
      console.error('Check error:', error);
      setLastCheckResult({ error: 'Fehler beim Ausführen' });
    } finally {
      setRunningCheck(false);
    }
  };

  const getDaysRemaining = (trialEnd) => {
    if (!trialEnd) return null;
    const end = new Date(trialEnd);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getStatusBadge = (daysRemaining) => {
    if (daysRemaining === null) return null;
    
    if (daysRemaining <= 0) {
      return (
        <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          Abgelaufen
        </span>
      );
    }
    if (daysRemaining <= 7) {
      return (
        <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          {daysRemaining} {daysRemaining === 1 ? 'Tag' : 'Tage'}
        </span>
      );
    }
    if (daysRemaining <= 30) {
      return (
        <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {daysRemaining} Tage
        </span>
      );
    }
    return (
      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
        <CheckCircle className="w-3 h-3" />
        {daysRemaining} Tage
      </span>
    );
  };

  // Group partners by urgency
  const expiredPartners = trialPartners.filter(p => getDaysRemaining(p.subscription?.trial_end) <= 0);
  const criticalPartners = trialPartners.filter(p => {
    const days = getDaysRemaining(p.subscription?.trial_end);
    return days > 0 && days <= 7;
  });
  const warningPartners = trialPartners.filter(p => {
    const days = getDaysRemaining(p.subscription?.trial_end);
    return days > 7 && days <= 30;
  });
  const healthyPartners = trialPartners.filter(p => {
    const days = getDaysRemaining(p.subscription?.trial_end);
    return days > 30;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-brand-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <button onClick={() => navigate('/admin')} className="text-white/70 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base md:text-xl font-bold text-white">Trial-Partner</h1>
          </div>
          <span className="text-white/50 text-xs md:text-sm hidden sm:block">{getVersionString()}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{expiredPartners.length}</p>
                <p className="text-sm text-gray-600">Abgelaufen</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{criticalPartners.length}</p>
                <p className="text-sm text-gray-600">Kritisch (≤7 Tage)</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{warningPartners.length}</p>
                <p className="text-sm text-gray-600">Warnung (≤30 Tage)</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{healthyPartners.length}</p>
                <p className="text-sm text-gray-600">Aktiv (&gt;30 Tage)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Manual Check Button */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Automatische Prüfung</h2>
              <p className="text-sm text-gray-600">
                Prüft alle Trial-Partner, sendet Erinnerungen und deaktiviert abgelaufene Konten
              </p>
            </div>
            <Button
              onClick={runTrialCheck}
              disabled={runningCheck}
              className="bg-brand-green hover:bg-brand-green-dark text-white"
            >
              {runningCheck ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Läuft...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  Jetzt prüfen
                </>
              )}
            </Button>
          </div>
          
          {lastCheckResult && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              {lastCheckResult.error ? (
                <p className="text-red-600">{lastCheckResult.error}</p>
              ) : (
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xl font-bold text-amber-600">{lastCheckResult.reminders_30_days || 0}</p>
                    <p className="text-xs text-gray-600">30-Tage Erinnerungen</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-orange-600">{lastCheckResult.reminders_7_days || 0}</p>
                    <p className="text-xs text-gray-600">7-Tage Erinnerungen</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-red-600">{lastCheckResult.reminders_1_day || 0}</p>
                    <p className="text-xs text-gray-600">1-Tag Erinnerungen</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900">{lastCheckResult.accounts_deleted || 0}</p>
                    <p className="text-xs text-gray-600">Konten gelöscht</p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-4">
            Hinweis: Diese Prüfung wird automatisch täglich um 8:00 Uhr ausgeführt.
          </p>
        </div>

        {/* Partners List */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">
              Alle Trial-Partner ({trialPartners.length})
            </h2>
          </div>
          
          {trialPartners.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <p className="text-gray-600">Keine Trial-Partner vorhanden</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {trialPartners.map(partner => {
                const daysRemaining = getDaysRemaining(partner.subscription?.trial_end);
                return (
                  <div key={partner.id} className="p-5 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">{partner.company_name}</h3>
                            {getStatusBadge(daysRemaining)}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {partner.contact_first_name} {partner.contact_last_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              {partner.email}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-sm text-gray-600">
                          <Calendar className="w-4 h-4 inline mr-1" />
                          Endet: {partner.subscription?.trial_end 
                            ? new Date(partner.subscription.trial_end).toLocaleDateString('de-DE')
                            : 'N/A'
                          }
                        </div>
                        <div className="flex gap-2 mt-2">
                          {partner.reminder_sent_30_days && (
                            <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-xs rounded">
                              30d gesendet
                            </span>
                          )}
                          {partner.reminder_sent_7_days && (
                            <span className="px-2 py-0.5 bg-orange-50 text-orange-600 text-xs rounded">
                              7d gesendet
                            </span>
                          )}
                          {partner.reminder_sent_1_day && (
                            <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded">
                              1d gesendet
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTrialManagementPage;
